import { AspectRatioProps, ContentTypeProps, PresserProps, WizardProps } from './Types'
import { Children, cloneElement } from 'react'
import { mergeProps } from '@react-aria/utils'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { SEOPlanningWizard } from 'components/Wizards/SEOPlanningWizard/SEOPlanningWizard'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { YoutubeScriptWizard } from 'components/Wizards/YoutubeScriptWizard/YoutubeScriptWizard'
import { TurnIntoWizard } from 'components/Wizards/TurnInto/TurnIntoWizard'
import { TextChannelKey, TurnInto } from 'types'
import { SourceType } from 'components/Wizards/TurnInto/Source/Types'
import { schemas } from 'components/Wizards/schemas/schemas'
import { useModals } from 'almanac-ui/Modal'
import { Wizard } from 'components/Wizards/Wizard'
import { ASPECT_RATIOS, AspectRatioKey } from 'components/NewFileModal/Types'
import { TextChannels } from '../../constants'
import { useCreateDesign } from 'screens/blaze/Home/components/Catalogue/components/useCreateDesign'
import { PressEvent, PressProps } from '@react-aria/interactions'
import { multiStepSchemas } from 'components/Wizards/schemas/multiStepSchemas'
import { MultiStepWizard } from 'components/Wizards/MultiStepWizard'
import { FormSchema } from 'components/Wizards/Wizard/Form/Types'
import { MultiStepInstagramPlanningWizard } from 'components/Wizards/InstagramPlanning/MultiStepInstagramPlanningWizard'
import { MultiStepFacebookPlanningWizard } from 'components/Wizards/FacebookPlanning/MultiStepFacebookPlanningWizard'
import { MultiStepLinkedInPlanningWizard } from 'components/Wizards/LinkedInPlanning/MultiStepLinkedInPlanningWizard'
import { MultiStepTwitterPlanningWizard } from 'components/Wizards/TwitterPlanning/MultiStepTwitterPlanningWizard'
import { MultiStepBlogPostWizard } from 'components/Wizards/MultiStepBlogPostWizard/MultiStepBlogPostWizard'
import { MultiStepSchema, StepSchema } from 'components/Wizards/schemas/Types'
import { BlogPostWizard } from 'components/Wizards/BlogPostWizard/BlogPostWizard'
import { Values } from 'components/Wizards/MultiStepBlogPostWizard/Types'
import { ContentPlanWizard } from 'components/Wizards/ContentPlanWizard/ContentPlanWizard'
import { MultiStepContentPlanSchema } from 'components/Wizards/schemas/ContentPlanSchema'
import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import { EmailNewsletterMultiStepSchema } from 'components/Wizards/schemas/EmailNewsletter'
import { MultiStepEmailNewsletterWizard } from 'components/Wizards/MultiStepEmailNewsletterWizard/MultiStepEmailNewsletterWizard'
import { useRootStore } from 'stores/RootStore'
import { TierUpgradeModal } from 'components/TierUpgradeModal/TierUpgradeModal'
import { useNeedsUpgradeForChannelType } from 'hooks/useChannelNeedsUpgrade'
import { useIsDesktopWizardFlow } from 'components/Wizards/DesktopWizards/useIsDesktopWizardFlow'
import { WizardOpener } from 'components/Wizards/DesktopWizards/WizardOpener/WizardOpener'
import { usePaywallGuard } from 'hooks/usePaywallGuard'

export const Presser = ({ children, ...pressProps }: PresserProps) => {
  const element = Children.only(children)

  return <>{cloneElement(element, mergeProps(children.props, pressProps))}</>
}

const WizardType = ({
  type,
  initialFormStatus,
  children,
  onPress: propsOnPress,
  isDisabled,
  onSuccess,
  schemaModifier = <T extends FormSchema | MultiStepSchema | StepSchema>(schema: T) => schema,
  initialValues,
  ...rest
}: WizardProps) => {
  const { openModal } = useModals()
  let onPress: PressProps['onPress']
  const {
    drawersStore: { closeAllDrawers },
  } = useRootStore()

  const { shouldForceUpgrade, currentTierPlan, nextTierPlan } = useNeedsUpgradeForChannelType()

  const { data: brandKits } = useBrandKits()
  const latestBrandKitId = brandKits?.[brandKits.length - 1]?.id

  const openWizard = (type: ChannelType | GenerationType) => {
    const multistepSchema = multiStepSchemas[type]

    if (multistepSchema) {
      const multiStepWizardSchema = schemaModifier(multistepSchema)
      onPress = () => {
        openModal(
          MultiStepWizard,
          {
            schema: multiStepWizardSchema,
            initialValues: { brand_kit_id: latestBrandKitId, ...initialValues },
            initialFormStatus,
            onSuccess,
            ...rest,
          },
          { underlayVariant: 'wizard' },
        )
      }
    } else {
      const schema = schemaModifier(schemas[type] as FormSchema<any>) as FormSchema<ChannelType | GenerationType>

      onPress = () =>
        openModal(
          Wizard,
          {
            schema,
            initialValues: { brand_kit_id: latestBrandKitId, ...initialValues },
            initialFormStatus,
            onSuccess,
            ...rest,
          },
          { underlayVariant: 'wizard' },
        )
    }
  }

  switch (type) {
    case GenerationType.TwitterPlanning:
      onPress = () =>
        openModal(
          MultiStepTwitterPlanningWizard,
          { initialValues: { brand_kit_id: latestBrandKitId, ...initialValues }, ...rest },
          { underlayVariant: 'wizard' },
        )
      break
    case GenerationType.FacebookPlanning:
      onPress = () =>
        openModal(
          MultiStepFacebookPlanningWizard,
          { initialValues: { brand_kit_id: latestBrandKitId, ...initialValues }, ...rest },
          { underlayVariant: 'wizard' },
        )
      break
    case GenerationType.LinkedInPlanning:
      onPress = () =>
        openModal(
          MultiStepLinkedInPlanningWizard,
          { initialValues: { brand_kit_id: latestBrandKitId, ...initialValues }, ...rest },
          { underlayVariant: 'wizard' },
        )
      break
    case GenerationType.SEOPlanning:
      onPress = () =>
        openModal(
          SEOPlanningWizard,
          { initialValues: { brand_kit_id: latestBrandKitId, ...initialValues }, ...rest },
          { underlayVariant: 'wizard' },
        )
      break
    case ChannelType.YoutubeScript:
      onPress = () =>
        openModal(YoutubeScriptWizard, { initialValues, onSuccess, ...rest }, { underlayVariant: 'wizard' })
      break

    case ChannelType.Newsletter:
      const emailNewsletterSchema = schemaModifier(EmailNewsletterMultiStepSchema)

      onPress = () =>
        openModal(
          MultiStepEmailNewsletterWizard,
          {
            schema: emailNewsletterSchema,
            initialValues: { brand_kit_id: latestBrandKitId, ...initialValues } as Values,
            onSuccess,
            ...rest,
          },
          { underlayVariant: 'wizard' },
        )
      break
    case GenerationType.ContentPlan:
      onPress = () =>
        openModal(
          ContentPlanWizard,
          { initialValues, schema: MultiStepContentPlanSchema, ...rest },
          { underlayVariant: 'wizard' },
        )
      break
    case ChannelType.BlogPost:
      const blogPostMultistepSchema = multiStepSchemas[ChannelType.BlogPost] as
        | MultiStepSchema<ChannelType.BlogPost>
        | undefined

      if (blogPostMultistepSchema) {
        const multiStepWizardSchema = schemaModifier(blogPostMultistepSchema)

        onPress = () =>
          openModal(
            MultiStepBlogPostWizard,
            {
              schema: multiStepWizardSchema,
              initialValues: { brand_kit_id: latestBrandKitId, ...initialValues } as Values,
              initialFormStatus,
              onSuccess,
              ...rest,
            },
            { underlayVariant: 'wizard' },
          )
      } else {
        const schema = schemaModifier(schemas[type] as FormSchema) as FormSchema<ChannelType | GenerationType>

        onPress = () =>
          openModal(
            BlogPostWizard,
            { schema, initialValues, initialFormStatus, onSuccess, ...rest },
            { underlayVariant: 'wizard' },
          )
      }
      break
    case GenerationType.InstagramPlanning:
      onPress = () =>
        openModal(
          MultiStepInstagramPlanningWizard,
          { initialValues: { brand_kit_id: latestBrandKitId, ...initialValues }, ...rest },
          { underlayVariant: 'wizard' },
        )
      break
    case GenerationType.Campaign:
      onPress = () =>
        openModal(TurnIntoWizard, { initialIsCampaign: true, initialValues, ...rest }, { underlayVariant: 'wizard' })
      break
    case TurnInto.VideoIntoContent:
      onPress = () =>
        openModal(
          TurnIntoWizard,
          { sourceType: SourceType.VideoFile, initialValues, ...rest },
          { underlayVariant: 'wizard' },
        )
      break
    case TurnInto.DocsIntoContent:
      onPress = () =>
        openModal(
          TurnIntoWizard,
          { sourceType: SourceType.File, initialValues, ...rest },
          { underlayVariant: 'wizard' },
        )
      break
    case TurnInto.AudioIntoContent:
      onPress = () =>
        openModal(
          TurnIntoWizard,
          { sourceType: SourceType.AudioFile, initialValues, ...rest },
          { underlayVariant: 'wizard' },
        )
      break
    case TurnInto.WebsitesIntoContent:
      onPress = () =>
        openModal(
          TurnIntoWizard,
          { sourceType: SourceType.Website, initialValues, ...rest },
          { underlayVariant: 'wizard' },
        )
      break
    default:
      openWizard(type as ChannelType | GenerationType)
  }

  if (shouldForceUpgrade(type) && nextTierPlan && currentTierPlan) {
    const originalOnPress = onPress

    onPress = (e) => {
      closeAllDrawers()
      openModal(TierUpgradeModal, {
        reason: 'feature_disabled',
        feature: 'Multiple Post Wizards',
        upgradeTier: nextTierPlan,
        currentTier: currentTierPlan,
        onUpgrade: () => originalOnPress?.(e),
      })
    }
  }

  const handlePress = (pressEvent: PressEvent) => {
    if (isDisabled) {
      return
    }
    onPress?.(pressEvent)
    propsOnPress?.(pressEvent)
  }

  const guardedHandlePress = usePaywallGuard(handlePress)

  return (
    <Presser isDisabled={isDisabled} onPress={guardedHandlePress} preventFocusOnPress>
      {children}
    </Presser>
  )
}

const AspectRatioType = ({
  type,
  children,
  schemaModifier,
  initialValues,
  onPress: propsOnPress,
  isDisabled,
  onSuccess,
  ...rest
}: AspectRatioProps) => {
  const { createDesign } = useCreateDesign({ schemaModifier, onSuccess })

  const guardedOnPress = usePaywallGuard((event) => {
    if (isDisabled) {
      return
    }
    createDesign({ aspectRatioKey: type, initialValues, ...rest })
    propsOnPress?.(event)
  })

  return (
    <Presser isDisabled={isDisabled} onPress={guardedOnPress} preventFocusOnPress>
      {children}
    </Presser>
  )
}

export const ContentType = ({ type, children, ...rest }: ContentTypeProps) => {
  const isAspectRatio = Object.keys(ASPECT_RATIOS).includes(type)
  const isWizard = Object.keys(TextChannels).includes(type)
  const isWizardFlow = useIsDesktopWizardFlow(type)

  if (isWizardFlow) {
    return (
      <WizardOpener type={type as AspectRatioKey} {...rest}>
        {children}
      </WizardOpener>
    )
  }

  if (isAspectRatio) {
    return (
      <AspectRatioType type={type as AspectRatioKey} {...rest}>
        {children}
      </AspectRatioType>
    )
  }

  if (isWizard) {
    return (
      <WizardType type={type as TextChannelKey} {...rest}>
        {children}
      </WizardType>
    )
  }

  return <>{children}</>
}
